import { lazy, Suspense, useEffect, useState } from 'react';
import { Alert } from 'reactstrap';
import Spinner from 'src/componentes/spinner';
import { Row, Col } from 'src/componentes/grid';
import { FormattedMessage, useIntl } from 'react-intl';

import { PontoControleEditForm } from 'src/paginas/gestao-risco/ponto-controle/edit';
import { capitalizeFirstLetter, lowerCaseAllLetter } from 'src/utils/string';
import ArquivoUploadAnexos from 'src/componentes/arquivo-upload/anexos';
import EditDialog from 'src/componentes/edit/edit-dialog';
import Tab from 'src/componentes/tabs/tab';
import Tabs from 'src/componentes/tabs/tabs';
import Input from 'src/componentes/input';
import Panel from 'src/componentes/panel';
import PontoControleLookup from 'src/lookups/ControleLookup';
import StatusLookup from 'src/lookups/status-risco-ponto-controle';
import { COLUNAS } from 'src/utils/constants';

import Schema from './RiscoPontoControleSchema';
import { getConfiguration } from 'src/services/commonRequests';
import ContentManager from 'src/componentes/content-manager';
import Confirm from 'src/componentes/message-box/confirm';
import InputNumber from 'src/componentes/input-number';
import { FUNCAO_CONTROLE } from 'src/paginas/gestao-risco/constants/BeltConstants';
import Checkbox from 'src/componentes/checkbox';
import Info from 'src/componentes/info';
import Tooltip from 'src/componentes/tooltip';
import { formatNumberWithSeparators } from 'src/utils/number';
import CustomInput from 'src/componentes/CustomInput';
import useAuth from 'src/hooks/useAuth';

const RevisaoPontoControleListView = lazy(() =>
  import(
    'src/paginas/gestao-risco/risco/RevisaoPontoControle/RevisaoPontoControleListView'
  )
);
const EfetividadeControleView = lazy(() =>
  import(
    'src/paginas/gestao-risco/risco/RevisaoPontoControle/EfetividadeControle/EfetividadeControleView'
  )
);
const IndicadoresRiscoControleView = lazy(() =>
  import(
    'src/paginas/gestao-risco/ponto-controle/controle-indicador-risco/view'
  )
);

const TabLoading = (
  <div
    style={{
      minHeight: 200,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <Spinner className="tw:size-8" color="primary" />
  </div>
);

const RiscoPontoControleForm = (props) => {
  const intl = useIntl();
  const {
    terms: resources,
    configurations,
    decimalSeparator,
    thousandsSeparator,
  } = useAuth();
  const tiposAnexos = configurations?.tiposAnexos;

  const { model, disabled, parameters, handleCloseEdit } = props;

  const [configuracao, setConfiguracao] = useState({});
  const [configuracaoRisco, setConfiguracaoRisco] = useState({});
  const [alterouEfetividade, setAlterouEfetividade] = useState(false);

  useEffect(() => {
    getConfiguration('PontoControleConfiguracao')(setConfiguracao)();
    getConfiguration('ItemConfiguracaoRisco')(setConfiguracaoRisco)();
  }, []);

  const handleChangePontoControle = (value) => {
    if (value == null) {
      model.getModel('periodicidade').requestChange(null);
      model.getModel('responsaveis').requestChange(null);
      model.getModel('anexoObrigatorio').requestChange(null);
      model.getModel('diasToleranciaRevisao').requestChange(null);
      model.getModel('tipoTolerancia').requestChange(null);
      model.getModel('calendario').requestChange(null);
      model.getModel('tipoEvidencias').requestChange(null);
    } else {
      model.getModel('periodicidade').requestChange(value.periodicidade);
      model.getModel('responsaveis').requestChange(value.responsaveis);
      model.getModel('anexoObrigatorio').requestChange(value.anexoObrigatorio);
      model
        .getModel('diasToleranciaRevisao')
        .requestChange(value.diasToleranciaRevisao);
      model.getModel('tipoTolerancia').requestChange(value.tipoTolerancia);
      model.getModel('calendario').requestChange(value.calendario);
      model.getModel('tipoEvidencias').requestChange(value.tipoEvidencias);
    }

    model.getModel('pontoControle').requestChange({
      id: value?.id,
      descricao: value?.descricao,
      agrupador: value?.agrupador,
    });
  };

  const setChangeEfetividade = () => {
    setAlterouEfetividade(true);
  };

  const closeModal = (closeModalChild) => {
    handleCloseEdit && handleCloseEdit(false, closeModalChild);
  };

  const pesoHabilitado = () => model.getModel('pesoHabilitado').value;

  const parsePeso = (value) => {
    if (value == null || value === '') return 0;

    const numberValue = Number(value);
    if (!isNaN(numberValue)) return numberValue;

    let normalizedValue = value.toString();
    if (thousandsSeparator) {
      normalizedValue = normalizedValue.split(thousandsSeparator).join('');
    }
    if (decimalSeparator) {
      normalizedValue = normalizedValue.split(decimalSeparator).join('.');
    }

    const parsedValue = Number(normalizedValue);
    return isNaN(parsedValue) ? 0 : parsedValue;
  };

  const formatPeso = (value) =>
    formatNumberWithSeparators(value, 2, decimalSeparator, thousandsSeparator);

  const requestChangeTipos = (tipo) => {
    let tiposModel = model.getModel('tiposSelecionados').value;

    if (!tiposModel) {
      tiposModel = [];
    }

    var tipoSelecionado = tiposModel.find((t) => t.id === tipo.id);

    if (!tipoSelecionado) {
      tipoSelecionado = {
        id: tipo.id,
        descricao: tipo.descricao,
        value: true,
      };
      tiposModel.push(tipoSelecionado);
    } else {
      tipoSelecionado.value = !tipoSelecionado.value;
    }
    model.getModel('tiposSelecionados').requestChange(tiposModel);
  };

  const getValueTipo = (tipo) => {
    if (!model.getModel('aplicarEfetividadeAoTipo')?.value) return false;

    const tiposModel = model.getModel('tiposSelecionados').value;
    if (tiposModel) {
      var tipoSelecionado = tiposModel.find((t) => t.id == tipo.id);
      return tipoSelecionado ? tipoSelecionado.value : false;
    }
    return false;
  };

  const id = model.getModel('id').value;
  const isNew = !id || id == 0;
  const idPontoControle = model.getValue('pontoControle')?.id;

  const isDisabled = disabled
    ? disabled
    : model && !model.getModel('permissions').getModel('allowEdit').value;

  let tipos = [];
  const pontoControleEditModel = model.getModel('pontoControleEdit');

  const currenttabIndex = props.currenttabIndex || 0;

  const funcao =
    model?.getModel('pontoControleEdit')?.value?.funcaoPontoControle;
  const pesoTotal =
    isNew && configuracao?.avaliarEfetividadeControleSeparadamente
      ? funcao?.id === FUNCAO_CONTROLE.CORRETIVO
        ? model.getModel('pesoTotalCorretivo').value
        : model.getModel('pesoTotalPreventivo').value
      : model.getModel('pesoTotal').value || 0;

  const controleCorretivo = funcao?.id === FUNCAO_CONTROLE.CORRETIVO;
  const controlePreventivo =
    !controleCorretivo && funcao?.id === FUNCAO_CONTROLE.PREVENTIVO;

  if (controleCorretivo) {
    tipos = model.getModel('tiposSeveridade')?.value || [];
  } else if (controlePreventivo) {
    tipos = model.getModel('tiposProbabilidade')?.value || [];
  }

  const statusControleId = model.getModel('status')?.value?.id;
  const controleAtivo = statusControleId === 1;
  const controleEncerrado = statusControleId === 2;
  const pesoItem = controleAtivo ? parsePeso(model.getModel('peso').value) : 0;
  const pesoDistribuido = Number((parsePeso(pesoTotal) + pesoItem).toFixed(2));
  const charCount = model?.getModel('descricao')?.value
    ? model.getModel('descricao').value.length
    : 0;

  return (
    <Tabs currenttabIndex={currenttabIndex}>
      <Tab key="1" label={intl.formatMessage({ id: 'label.resumo' })}>
        <Panel>
          <Row>
            <Col md={5}>
              <PontoControleLookup
                disabled={isDisabled || id > 0}
                model={{
                  label: capitalizeFirstLetter(resources.pontodecontrole),
                  value: model.getModel('pontoControle').value,
                  requestChange: handleChangePontoControle,
                  errors: model.getModel('pontoControle').errors,
                }}
                data={{
                  riscoId: parameters.riscoId,
                  todosTipos: false,
                  pontoControleId: idPontoControle,
                }}
                required
              />
            </Col>

            {pesoHabilitado() && (
              <Col md={3}>
                <InputNumber
                  disabled={
                    isDisabled ||
                    model.getModel('pontoControle')?.value?.agrupador
                  }
                  precision={2}
                  symbolEnd="%"
                  style={{ height: '38px' }}
                  model={{
                    label: intl.formatMessage({ id: 'peso' }) + ' (%)',
                    value: model.getModel('peso').value,
                    requestChange: model.getModel('peso').requestChange,
                    errors: model.getModel('peso').errors,
                  }}
                />
              </Col>
            )}
            <Col md={3}>
              <StatusLookup
                disabled={isDisabled}
                model={model.getModel('status')}
                required
              />
            </Col>
            {id > 0 && (
              <Col md={1}>
                <ArquivoUploadAnexos
                  tipoAnexo={tiposAnexos.riscoPontoControle}
                  formGroupStyle={{ marginBottom: 0 }}
                  model={model.getModel('arquivos')}
                />
              </Col>
            )}
          </Row>
          {pesoHabilitado() && !isNew && controleEncerrado && (
            <Row>
              <Col md={12}>
                <Alert color="warning">
                  <p>
                    {intl.formatMessage(
                      {
                        id: 'msgControleEncerradoNaoConsideradoCalculoPeso',
                      },
                      {
                        pontodecontrole: lowerCaseAllLetter(
                          resources.pontodecontrole
                        ),
                      }
                    )}
                  </p>
                </Alert>
              </Col>
            </Row>
          )}
          {pesoHabilitado() &&
            !controleEncerrado &&
            (!isNew || funcao) &&
            pesoDistribuido != 100 && (
              <Row>
                <Col md={12}>
                  <Alert color="warning">
                    <p>
                      {funcao?.id === FUNCAO_CONTROLE.PREVENTIVO
                        ? intl.formatMessage(
                            {
                              id: 'msgValidacaoAtencaoPesoTodosControlesNaoTotalizam100Preventivo',
                            },
                            { pontosdecontrole: resources.pontosdecontrole }
                          ) +
                          ' ' +
                          formatPeso(pesoDistribuido) +
                          '%'
                        : intl.formatMessage(
                            {
                              id: 'msgValidacaoAtencaoPesoTodosControlesNaoTotalizam100Corretivo',
                            },
                            { pontosdecontrole: resources.pontosdecontrole }
                          ) +
                          ' ' +
                          formatPeso(pesoDistribuido) +
                          '%'}
                    </p>
                  </Alert>
                </Col>
              </Row>
            )}
          <Row>
            <Col md={12}>
              <Input
                rows={2}
                type="textarea"
                disabled={isDisabled}
                model={model.getModel('descricao')}
                maxLength={300}
              />
              <span>
                {intl.formatMessage(
                  { id: 'labelMsgQtdadeCaracteresNome' },
                  { charCount: charCount }
                )}
              </span>
            </Col>
          </Row>
        </Panel>

        {!isNew &&
          !model.getModel('pontoControle')?.value?.agrupador &&
          !model.getModel('probabilidadeCalculadaPorFormula')?.value &&
          configuracao?.avaliarEfetividadeControle &&
          configuracao?.aplicarEfetividadeConformeTipos && (
            <Tooltip
              disabled={tipos && tipos.length > 0}
              text={
                !controleCorretivo && !controlePreventivo
                  ? intl.formatMessage(
                      {
                        id: 'msgFuncaoControleNaoDefinidaParaHabilitarEfetividadePorTipo',
                      },
                      {
                        severidade: resources.severidade,
                        probabilidade: resources.probabilidade,
                        pontodecontrole: resources.pontodecontrole,
                      }
                    )
                  : !tipos || tipos.length === 0
                  ? intl.formatMessage(
                      {
                        id: 'hintCadastrarTiposParaAplicarEfetividadeConformeSelecao',
                      },
                      {
                        tipocontrole: controleCorretivo
                          ? resources.severidade
                          : resources.probabilidade,
                      }
                    )
                  : null
              }
            >
              <div>
                <Panel
                  header={intl.formatMessage(
                    { id: 'labelEfetividadeApliacadaFuncaoControle' },
                    { pontodecontrole: resources.pontodecontrole }
                  )}
                  headerStyle={
                    (!controleCorretivo && !controlePreventivo) || isDisabled
                      ? { opacity: 0.5, color: '#60717d' }
                      : { opacity: 1 }
                  }
                >
                  <Row>
                    <Col className="d-flex inline" md={12}>
                      {(controleCorretivo || controlePreventivo) && (
                        <label className="tw:me-2">
                          {controleCorretivo
                            ? intl.formatMessage(
                                {
                                  id: 'labelHabilitarEfetividadeAplicadaTipoSeveridade',
                                },
                                { severidade: resources.severidade }
                              )
                            : intl.formatMessage(
                                {
                                  id: 'labelHabilitarEfetividadeAplicadaTipoProbabilidade',
                                },
                                { probabilidade: resources.probabilidade }
                              )}
                        </label>
                      )}
                      {(controleCorretivo || controlePreventivo) && (
                        <CustomInput
                          type="switch"
                          id={`switch-visible-subprojects`}
                          name="customSwitch"
                          disabled={isDisabled || !tipos || tipos.length === 0}
                          checked={
                            model.getModel('aplicarEfetividadeAoTipo')?.value &&
                            tipos &&
                            tipos.length > 0
                          }
                          onChange={() => {
                            model
                              .getModel('aplicarEfetividadeAoTipo')
                              .requestChange(
                                !model.getModel('aplicarEfetividadeAoTipo')
                                  .value
                              );
                          }}
                        />
                      )}
                      <Info
                        className="ml-2"
                        text={intl.formatMessage(
                          {
                            id: controlePreventivo
                              ? 'hintHabilitarEfetividadePorTipoRiscoControleProbabilidade'
                              : 'hintHabilitarEfetividadePorTipoRiscoControleSeveridade',
                          },
                          {
                            severidade: resources.severidade,
                            probabilidade: resources.probabilidade,
                            pontodecontrole: resources.pontodecontrole,
                          }
                        )}
                      />
                    </Col>
                  </Row>
                  <Row className="pt-2 pl-2">
                    {tipos &&
                      tipos.map((tipo) => (
                        <Col key={tipo.id} md={3}>
                          <Checkbox
                            styleLabel={{ fontWeight: 'bold' }}
                            disabled={
                              !model.getModel('aplicarEfetividadeAoTipo')
                                ?.value || isDisabled
                            }
                            model={{
                              label: tipo.nome,
                              value: getValueTipo(tipo),
                              requestChange: (value) =>
                                requestChangeTipos(tipo, value),
                            }}
                          />
                        </Col>
                      ))}
                  </Row>
                </Panel>
              </div>
            </Tooltip>
          )}

        {id > 0 && (
          <Panel
            header={
              <FormattedMessage
                id="pontoControle"
                values={{
                  pontodecontrole: capitalizeFirstLetter(
                    resources.pontodecontrole
                  ),
                }}
              />
            }
          >
            <PontoControleEditForm
              model={pontoControleEditModel}
              resources={resources}
              setChangeEfetividade={setChangeEfetividade}
              intl={intl}
              disabled={isDisabled}
              showConfirmMsg={true}
              showRevisoesControle={false}
              riscoControleView={true}
            />
          </Panel>
        )}
      </Tab>
      {id > 0 && (
        <Tab
          key="2"
          label={intl.formatMessage(
            { id: 'label.revisaoPontoControle' },
            { revisaodecontrole: resources.revisaodecontrole }
          )}
        >
          <Suspense fallback={TabLoading}>
            <RevisaoPontoControleListView
              id={id}
              tag={COLUNAS.RISCO_REVISOES_PONTO_CONTROLE}
              disabled={isDisabled ? isDisabled : false}
              showDelete={isDisabled ? false : true}
              showNew={isDisabled ? false : true}
              showTitle={false}
              idRiscoPontoControle={id}
              parameters={{
                idRisco: parameters.riscoId,
                idRiscoPontoControle: id,
              }}
            />
          </Suspense>
        </Tab>
      )}
      {id > 0 && configuracao && configuracao.avaliarEfetividadeControle && (
        <Tab
          key="3"
          label={intl.formatMessage(
            { id: 'efetividadeControle' },
            { pontodecontrole: resources.pontodecontrole }
          )}
        >
          <Suspense fallback={TabLoading}>
            <EfetividadeControleView
              funcaoControle={
                pontoControleEditModel.getModel('funcaoPontoControle').value
              }
              alterouEfetividade={alterouEfetividade}
              riscoControleId={id}
              disabled={isDisabled ? isDisabled : false}
              showDelete={isDisabled ? false : true}
              showNew={isDisabled ? false : true}
            />
          </Suspense>
        </Tab>
      )}
      {id > 0 &&
        configuracaoRisco &&
        configuracaoRisco.habilitarIndicadoresRisco &&
        model.getModel('pontoControleEdit').getModel('frequenciaControle')
          .value &&
        model.getModel('pontoControleEdit').getModel('frequenciaControle').value
          .id == 2 && (
          <Tab
            label={intl.formatMessage(
              { id: 'indicadorRisco' },
              { risco: resources.risco }
            )}
            key="4"
          >
            <Suspense fallback={TabLoading}>
              <IndicadoresRiscoControleView
                controleId={idPontoControle}
                closeModal={closeModal}
              />
            </Suspense>
          </Tab>
        )}
    </Tabs>
  );
};

const RiscoPontoControleEdit = (props) => {
  const { terms: resources } = useAuth();
  const { parameters } = props;

  const handleConfirmeBeforeSave = (
    item,
    handleSave,
    intl,
    fechar,
    resourcesAtual,
    modelInicial,
    showErrors,
    afterSave
  ) => {
    if (item && item.pontoControle && item.pontoControle.agrupador) {
      const msgId =
        item.pontoControle.totalFilhos <= 1
          ? 'msgVinculacaoAgrupadorControlesRiscoSingular'
          : 'msgVinculacaoAgrupadorControlesRisco';
      ContentManager.addContent(
        <Confirm
          message={intl
            .formatMessage(
              { id: msgId },
              {
                pontosdecontrole: lowerCaseAllLetter(
                  resourcesAtual.pontosdecontrole
                ),
                pontodecontrole: lowerCaseAllLetter(
                  resourcesAtual.pontodecontrole
                ),
                risco: lowerCaseAllLetter(resourcesAtual.risco),
              }
            )
            .replaceAll('{totalfilhos}', `${item.pontoControle.totalFilhos}`)}
          handleConfirm={() => {
            handleSave(fechar);
          }}
        />
      );
    } else {
      handleSave(fechar);
      afterSave && afterSave();
    }
  };

  return (
    <EditDialog
      url="/RiscoPontoControle"
      title={capitalizeFirstLetter(resources.pontodecontrole)}
      parameters={parameters}
      formComponent={RiscoPontoControleForm}
      schema={Schema}
      width="85%"
      maxHeight="93%"
      handleConfirmeBeforeSave={handleConfirmeBeforeSave}
      resources={resources}
      {...props}
    />
  );
};

export default RiscoPontoControleEdit;
